<script setup>
import SectionHero from '@/components/SectionHero.vue';
import SectionHalfText from '@/components/SectionHalfText.vue';
import FlatCard from '@/components/FlatCard.vue';

import {useRouting} from "@/composables/routing.js";
const {href,url} = useRouting();

import { Head } from '@inertiajs/vue3'
import { Link } from '@inertiajs/vue3';
</script>

<template>
    <Head>
        <title>Features of our managed cloud</title>
        <meta name="description" content="Discover the features of our managed cloud. Spaces, databases, routes and domains">
        <link rel="canonical" :href="url('/features', 'en')">
        <link rel="alternate" hreflang="de" :href="url('/features', 'de')">
        <link rel="alternate" hreflang="en" :href="url('/features', 'en')">
    </Head>
    <div>
        <section-hero
            title="Features of our managed cloud"
            subtitle="Our managed cloud provides the perfect combination of performance, security, and
                      flexibility. Whether it's a website, online store, agency projects, or SaaS applications, we have
                      the ideal solution for you."
        >
        </section-hero>

        <section-half-text class="mt-10"
                           id="spaces"
                           title="Spaces"
        >
            <template #default>
                <p class="text-lg mt-10">Spaces are modern, container-based web spaces that combine the scalability and guaranteed performance of the cloud with the ease of configuration of traditional web hosting.</p>
                <p class="text-lg mt-2">
                    Container technology ensures resource isolation, so you always have exclusive access to the allocated performance. At the same time, if the current node fails, the container can be automatically restarted on another cluster node.
                    This process happens seamlessly and automatically.
                </p>
                <p class="text-lg mt-2">
                    SFTP and SSH access make it easy to upload your application and provide the ability to access it during runtime. The Space is ideal for small to medium-sized web projects that require a simple and flexible hosting solution.
                </p>
            </template>
            <template #extra>
                <div class="col-span-2 flex items-center">
                    <img src="/assets/space-ssh.png" alt="Spaces" class="w-full xl:mx-10 mx-auto rounded-2xl shadow-2xl max-w-full h-auto" />
                </div>
            </template>
        </section-half-text>

        <section-half-text class="mt-32 lg:mt-56"
                           id="databases"
                           text-right
                           title="Databases"
        >
            <template #default>
                <p class="text-lg mt-10">Modern web applications require powerful databases. Database queries have a critical impact on your application's performance.</p>
                <p class="text-lg mt-2">In our managed cloud, you can create a new database with just a few clicks. In the background, a dedicated database server is provisioned for you. The allocated performance can be scaled at any time.</p>
                <p class="text-lg mt-2">We do not charge any additional fees. You only pay for the booked performance and storage space.</p>
            </template>
            <template #extra>
                <div class="col-span-2 flex items-center">
                    <img src="/assets/create-database.png" alt="Databases" class="w-full xl:mx-10 mx-auto rounded-2xl shadow-2xl" />
                </div>
            </template>
        </section-half-text>

        <section-half-text class="mt-32 lg:mt-56"
                           id="routes"
                           title="Routes"
        >
            <template #default>
                <p class="text-lg mt-10">With Routes, you can make your application accessible through different domains and subdomains. Additionally, you can configure path-based routing. Both destinations and redirects can be defined.</p>
                <p class="text-lg mt-2">
                    All routes are resolved on our redundant load balancer infrastructure, ensuring high availability and performance for your application. Despite this, the configuration remains simple and can be completed in just a few clicks.
                </p>
                <p class="text-lg mt-2">There are no fees for Routes. A free SSL certificate is included.</p>
            </template>
            <template #extra>
                <div class="col-span-2 flex items-center">
                    <img src="/assets/create-route.png" alt="Routes" class="w-full xl:mx-10 mx-auto rounded-2xl shadow-2xl" />
                </div>
            </template>
        </section-half-text>

        <section-half-text class="mt-32 lg:mt-56"
                           id="domains"
                           text-right
                           title="Domains"
        >
            <template #default>
                <p class="text-lg mt-10">
                    Our Domain Robot allows you to easily and quickly register your desired domains. Registrations and transfers are executed in real-time. You can centrally manage and reuse the contact information associated with your domain.
                </p>
                <p class="text-lg mt-2">
                    We offer a wide selection of domain extensions and attractive prices starting from the first domain. This makes our offering particularly appealing to agencies and resellers. Optionally, you can use our REST API to automate domain
                    processes.
                </p>
                <p class="text-lg mt-2">You can define your own nameservers or use a global Anycast DNS zone managed by us.</p>
                <Link :href="href('/pricing#domains')" class="block text-lg underline font-bold mt-4">
                    To the detailed price table
                </Link>
            </template>
            <template #extra>
                <flat-card title=".DE from €4.19" text="Register your .de domain for just €4.19 (new registration +€0.80)" />
                <flat-card title=".EU only €8.86" text="Register your .eu domain for just €8.86" />
                <flat-card title=".AT only €12.86" text="Register your .at domain for just €12.86" />
                <flat-card title=".COM only €14.55" text="Register your .com domain for just €14.55" />
                <flat-card title=".NET only €16.34" text="Register your .net domain for just €16.34" />
                <flat-card title=".ORG only €14.01" text="Register your .org domain for just €14.01" />
            </template>
        </section-half-text>
    </div>
</template>
